import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getStudent, uploadStudentProfileImage } from '../../services/studentService';
import { invalidateCachedProfileImage } from '../../utils/profileImageCache';
import Loader from '../common/Loader';
import Alert from '../common/Alert';
import ProfileAvatar from '../common/ProfileAvatar';

import { formatDisplayDate } from '../../utils/dateUtils';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return formatDisplayDate(iso);
  } catch { return '—'; }
}

function complianceBadge(score) {
  if (score === null || score === undefined)
    return 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-slate-700 dark:text-slate-400 dark:border-slate-600';
  if (score >= 80)
    return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800';
  if (score >= 50)
    return 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800';
  return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
}

export default function ViewStudent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [imageRefreshKey, setImageRefreshKey] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await getStudent(id);
        setStudent(data.data);
      } catch {
        navigate('/students');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setAlert({ type: 'error', message: 'Please select a PNG, JPEG, GIF, or WEBP image.' });
      return;
    }
    setUploading(true);
    setAlert(null);
    try {
      await uploadStudentProfileImage(id, file);
      invalidateCachedProfileImage('student', Number(id));
      setStudent(prev => ({ ...prev, has_profile_image: true }));
      setImageRefreshKey((k) => k + 1);
      setAlert({ type: 'success', message: 'Profile image updated' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to upload image.' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading) return <Loader text="Loading student..." />;
  if (!student) return null;

  const score = student.compliance_score;

  return (
    <div className="mx-auto w-full min-w-0 max-w-[min(100%,56rem)] space-y-5">
      {alert && (
        <div className="fixed top-20 right-4 z-50 w-[calc(100vw-2rem)] max-w-sm sm:max-w-96">
          <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to="/students"
            className="p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">Student Details</h1>
        </div>
        <div className="flex items-center gap-2.5">
          <Link
            to={`/students/${id}/files`}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl border border-gray-300 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 text-sm font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
            </svg>
            <span className="hidden sm:inline">File Cabinet</span>
          </Link>
          <Link
            to={`/students/${id}/jobs`}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl border border-gray-300 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 text-sm font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="hidden sm:inline">Jobs</span>
          </Link>
          <Link
            to={`/students/${id}/information`}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="hidden sm:inline">View Compliance</span>
          </Link>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden">
        {/* Avatar header */}
        <div className="bg-gradient-to-br from-indigo-50 dark:from-indigo-600/20 to-white dark:to-slate-800 px-4 sm:px-6 py-6 sm:py-8 flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-5 text-center sm:text-left">
          <div className="relative flex-shrink-0 group">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden shadow-lg shadow-indigo-500/20">
              <ProfileAvatar
                type="student"
                id={student.id}
                variant="solid"
                hasProfileImage={student.has_profile_image}
                refreshKey={imageRefreshKey}
              />
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute inset-0 rounded-2xl bg-black/0 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer disabled:cursor-wait"
            >
              {uploading ? (
                <svg className="w-6 h-6 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <div className="flex flex-col items-center gap-0.5">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-white text-[10px] font-medium">Change</span>
                </div>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.gif,.webp"
              className="hidden"
              onChange={handleImageUpload}
            />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white break-words">{student.student_name}</h2>
            <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">
              SPED Teacher: {student.sped_teacher?.name || student.case_manager || '—'}
            </p>
            <div className="flex items-center gap-2 mt-2 flex-wrap justify-center sm:justify-start">
              {student.student_id && (
                <span className="px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 text-xs font-mono font-medium border border-slate-200 dark:border-slate-600">
                  {student.student_id}
                </span>
              )}
              <span className="px-2.5 py-1 rounded-full bg-indigo-100 dark:bg-indigo-600/20 text-indigo-600 dark:text-indigo-300 text-xs font-medium border border-indigo-200 dark:border-indigo-500/30">
                Grade {student.grade}
              </span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${complianceBadge(score)}`}>
                {score !== null && score !== undefined ? `${score}% Compliance` : 'No Score'}
              </span>
            </div>
          </div>
        </div>

        {/* Details grid */}
        <div className="px-4 sm:px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <InfoField label="Student ID" value={student.student_id || '—'} />
          <InfoField label="Date of Birth" value={formatDate(student.date_of_birth)} />
          <InfoField label="IEP Due Date" value={formatDate(student.iep_due_date)} />
          <InfoField label="Reevaluation Due Date" value={formatDate(student.reevaluation_due_date)} />
          <InfoField label="Compliance Score" value={score !== null && score !== undefined ? `${score} / 100` : '—'} />
          <InfoField label="Last Updated" value={student.last_updated ? new Date(student.last_updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} />
          <InfoField label="Created By" value={student.created_by?.name || '—'} />
          <InfoField label="Created At" value={student.created_at ? student.created_at.split('T')[0] : '—'} />
          <InfoField label="Updated By" value={student.updated_by?.name || '—'} />
          <InfoField label="Updated At" value={student.updated_at ? student.updated_at.split('T')[0] : '—'} />
        </div>
      </div>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400 dark:text-slate-500 font-medium uppercase tracking-wider mb-1">{label}</p>
      <p className="text-gray-700 dark:text-slate-200 text-sm break-words">{value}</p>
    </div>
  );
}